import { ApiService } from '@/support/ApiService';
import type { components } from '@/generated/internal-api/openapi-types';

type StudyApiId = components['schemas']['StudyApiId.schema'];
type AgentSessionApiId = components['schemas']['AgentSessionApiId.schema'];

/**
 * Thin client for the agent kickoff/cancel/get endpoints exposed by the
 * internal API. The streaming side runs over SSE — see useAgentStream.
 */
export class AgentSessionService {
  static createPulseSession(
    body: { study_api_id: StudyApiId; user_prompt?: string | null; agent?: string | null },
    signal?: AbortSignal
  ) {
    return ApiService.call({
      endpoint: 'POST /api/v1/ai/agents/pulse/sessions',
      body,
      signal,
      options: { onError: { toast: false, throw: false, capture: true, slug: 'agent-session-create-pulse' } },
    });
  }

  static createStudyProtocolSession(body: { url: string }, signal?: AbortSignal) {
    return ApiService.call({
      endpoint: 'POST /api/v1/ai/agents/study-protocol-create/sessions',
      body,
      signal,
      options: {
        onError: { toast: false, throw: false, capture: true, slug: 'agent-session-create-study-protocol' },
      },
    });
  }

  static presignStudyProtocolUpload(body: { file_name: string }, signal?: AbortSignal) {
    return ApiService.call({
      endpoint: 'POST /api/v1/ai/agents/study-protocol-create/uploads',
      body,
      signal,
      options: {
        onError: { toast: false, throw: false, capture: true, slug: 'agent-study-protocol-upload' },
      },
    });
  }

  static getSession(sessionApiId: AgentSessionApiId, signal?: AbortSignal) {
    return ApiService.call({
      endpoint: 'GET /api/v1/ai/sessions/{sessionApiId}',
      path: { sessionApiId },
      signal,
      options: { onError: { toast: false, throw: false, capture: true, slug: 'agent-session-get' } },
    });
  }

  static cancelSession(sessionApiId: AgentSessionApiId, signal?: AbortSignal) {
    return ApiService.call({
      endpoint: 'POST /api/v1/ai/sessions/{sessionApiId}/cancel',
      path: { sessionApiId },
      signal,
      options: { onError: { toast: false, throw: false, capture: true, slug: 'agent-session-cancel' } },
    });
  }
}
